import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet.markercluster";
import { MapPin, X, ArrowRight, Search, Compass, Loader2 } from "lucide-react";
import { locationsApi, discoveryApi, apiErrorMessage } from "@/api/client";
import { ProspectCard } from "@/components/ProspectCard";
import { AddToLeadsDialog } from "@/components/forms/AddToLeadsDialog";
import { accent } from "@/theme/themeTokens";

const BUCKET_COLOR = { high: "#059669", medium: "#D97706", low: "#64748B", unranked: "#94A3B8" };
const DEFAULT = { lat: 51.5074, lng: -0.1278, zoom: 13 };

function knownIcon() {
  return L.divIcon({ className: "", iconSize: [26, 26], iconAnchor: [13, 26], html:
    `<div style="width:26px;height:26px"><svg viewBox="0 0 24 24" width="26" height="26" fill="${accent.base}" stroke="#fff" stroke-width="1.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="#fff" stroke="none"/></svg></div>` });
}
function prospectIcon(p) {
  const color = p.is_suppressed ? "#94A3B8" : (BUCKET_COLOR[p.fhrs_priority_bucket] || "#94A3B8");
  return L.divIcon({ className: "", iconSize: [22, 22], iconAnchor: [11, 22], html:
    `<div style="width:22px;height:22px"><svg viewBox="0 0 24 24" width="22" height="22" fill="#fff" stroke="${color}" stroke-width="3"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2" fill="${color}" stroke="none"/></svg></div>` });
}

export default function MapPage() {
  const navigate = useNavigate();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const knownLayer = useRef(null);
  const prospectLayer = useRef(null);
  const [selectedKnown, setSelectedKnown] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [addProspect, setAddProspect] = useState(null);
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [capped, setCapped] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const loadKnown = useCallback(async () => {
    const r = await locationsApi.listAll({ has_coords: true });
    const map = mapRef.current;
    if (!map || !knownLayer.current) return;
    knownLayer.current.clearLayers();
    r.data.items.forEach((loc) => {
      const m = L.marker([loc.latitude, loc.longitude], { icon: knownIcon() });
      m.on("click", () => { setSelectedProspect(null); setSelectedKnown(loc); });
      knownLayer.current.addLayer(m);
    });
  }, []);

  const loadProspects = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;
    setLoading(true); setUnavailable(false);
    const b = map.getBounds();
    try {
      const r = await discoveryApi.viewport({ north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest(), size: 25 });
      prospectLayer.current.clearLayers();
      setCapped(!!r.data.capped);
      r.data.items.forEach((p) => {
        if (p.latitude == null || p.longitude == null || p.is_known) return;
        const m = L.marker([p.latitude, p.longitude], { icon: prospectIcon(p) });
        m.on("click", () => { setSelectedKnown(null); setSelectedProspect(p); });
        prospectLayer.current.addLayer(m);
      });
    } catch (err) {
      if (err?.response?.data?.error?.code === "FSA_UNAVAILABLE") setUnavailable(true);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { zoomControl: true }).setView([DEFAULT.lat, DEFAULT.lng], DEFAULT.zoom);
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' }).addTo(map);
    knownLayer.current = L.layerGroup().addTo(map);
    prospectLayer.current = L.markerClusterGroup({ maxClusterRadius: 50 });
    map.addLayer(prospectLayer.current);
    loadKnown();
    return () => { map.remove(); mapRef.current = null; };
  }, [loadKnown]);

  const searchPostcode = async (e) => {
    e.preventDefault();
    if (!postcode.trim()) return;
    setLoading(true); setUnavailable(false);
    try {
      const r = await discoveryApi.byPostcode({ postcode: postcode.trim(), radius_km: 2, size: 25 });
      if (r.data.centre) mapRef.current.setView([r.data.centre.lat, r.data.centre.lng], 15);
      prospectLayer.current.clearLayers();
      r.data.items.forEach((p) => {
        if (p.latitude == null || p.is_known) return;
        const m = L.marker([p.latitude, p.longitude], { icon: prospectIcon(p) });
        m.on("click", () => { setSelectedKnown(null); setSelectedProspect(p); });
        prospectLayer.current.addLayer(m);
      });
      setCapped(!!r.data.capped);
    } catch (err) {
      setUnavailable(err?.response?.data?.error?.code === "FSA_UNAVAILABLE");
    } finally { setLoading(false); }
  };

  return (
    <div className="relative h-[calc(100vh-3.5rem)] md:h-screen" data-testid="map-view">
      <div ref={mapEl} className="h-full w-full" data-testid="leaflet-map" />

      {/* Search + discover controls */}
      <div className="absolute inset-x-0 top-0 z-[500] flex gap-2 p-3">
        <form onSubmit={searchPostcode} className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="map-postcode-input" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Postcode…"
            className="min-h-[44px] w-full rounded-lg border border-slate-200 bg-white/95 pl-9 pr-3 text-sm shadow-sm backdrop-blur focus:outline-none focus:ring-2 focus:ring-teal-600" />
        </form>
        <button data-testid="map-discover-btn" onClick={loadProspects} className="flex min-h-[44px] items-center gap-1.5 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white shadow-sm hover:bg-teal-800">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Compass className="h-4 w-4" />} Find prospects
        </button>
      </div>

      {capped && <div className="absolute left-1/2 top-16 z-[500] -translate-x-1/2 rounded-full bg-slate-900/90 px-3 py-1 text-xs font-medium text-white">Many results — narrow the area for more</div>}
      {unavailable && <div data-testid="map-fsa-unavailable" className="absolute left-1/2 top-16 z-[500] -translate-x-1/2 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white">FSA temporarily unavailable</div>}

      {/* Known location sheet */}
      {selectedKnown && (
        <BottomSheet testid="map-bottom-sheet" onClose={() => setSelectedKnown(null)} title={selectedKnown.name} subtitle={selectedKnown.organisation_name}>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            {selectedKnown.open_opportunity_count > 0 && <span className="rounded bg-teal-50 px-2 py-0.5 font-semibold text-teal-700">{selectedKnown.open_opportunity_count} open opps</span>}
            {selectedKnown.is_suppressed && <span data-testid="map-sheet-suppressed" className="rounded bg-red-50 px-2 py-0.5 font-semibold text-red-700">Suppressed</span>}
          </div>
          <button data-testid="map-sheet-view-lead" onClick={() => navigate(`/leads/${selectedKnown.organisation_id}`)} className="mt-4 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800">View lead detail <ArrowRight className="h-4 w-4" /></button>
        </BottomSheet>
      )}

      {/* Prospect sheet */}
      {selectedProspect && (
        <BottomSheet testid="map-prospect-sheet" onClose={() => setSelectedProspect(null)} bare>
          <ProspectCard p={selectedProspect} onAdd={(p) => { setSelectedProspect(null); setAddProspect(p); }} />
        </BottomSheet>
      )}

      <AddToLeadsDialog open={!!addProspect} onOpenChange={(v) => !v && setAddProspect(null)} prospect={addProspect} onDone={() => { loadKnown(); loadProspects(); }} />
    </div>
  );
}

function BottomSheet({ testid, onClose, title, subtitle, children, bare }) {
  return (
    <div data-testid={testid} className="absolute inset-x-0 bottom-0 z-[600] rounded-t-2xl border-t border-slate-200 bg-white p-5 shadow-2xl md:inset-x-auto md:bottom-4 md:right-4 md:w-96 md:rounded-2xl md:border">
      <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-slate-300 md:hidden" />
      <button data-testid="map-sheet-close" onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
      {!bare && <><h3 className="pr-8 font-heading text-lg font-extrabold text-slate-900">{title}</h3><p className="text-sm text-slate-500">{subtitle || "—"}</p></>}
      {children}
    </div>
  );
}
